import * as React from 'react';

import { useForm } from 'react-hook-form';

import useStatus from 'hooks/use-status';
import { login } from 'services/srp-auth';
import { useTurnstileValidation } from 'hooks/use-turnstile-validation';

import { GridBox } from 'components/box';
import Button from 'components/button';
import FormErrors from 'components/form-errors';
import Input from 'components/inputs/floating';
import Logo from 'components/logo';
import StatusText from 'components/status-text';
import { Link, Text } from 'components/text';
import Turnstile from 'components/turnstile';
import useClientId from 'hooks/use-client-id';
import * as Styles from './styles';

interface FormData {
  email: string;
  password: string;
}

const LoginForm: React.FC = () => {
  const { status, Transition } = useStatus();
  const [apiError, setApiError] = React.useState('');
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(
    null,
  );
  const { validateToken } = useTurnstileValidation();
  const { register, handleSubmit, errors } = useForm<FormData>();

  const client = useClientId();
  const isTcDeluxe = client.includes('deluxe');
  const AllowSignup = client !== 'mi' && !isTcDeluxe;

  const onSubmit = async (data: FormData) => {
    if (!turnstileToken) {
      setApiError('Please complete the verification');
      return;
    }

    setApiError('');
    Transition.loading();

    const isValid = await validateToken(turnstileToken);
    if (!isValid) {
      setApiError('Verification failed. Please try again.');
      Transition.error();
      return;
    }

    login(data).then((res) => {
      switch (res.status) {
        case 'error':
          setApiError(res.msg);
          Transition.error();
          break;
        case 'redirect':
          window.location.replace(res.uri);
          break;
        default:
          Transition.success();
      }
    });
  };

  return (
    <Styles.Wrapper style={{ background: isTcDeluxe ? '#282828' : 'auto' }}>
      <Logo mb={4} />
      {apiError && (
        <Text color="error.dark" fontSize={1} fontWeight="bold" mb={3}>
          {apiError}
        </Text>
      )}
      <FormErrors errors={errors} />
      <Styles.Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="Email"
          ref={register({ required: true })}
          inputProps={{
            name: 'email',
            type: 'email',
          }}
        />

        <Input
          label="Password"
          ref={register({ required: true })}
          inputProps={{
            name: 'password',
            type: 'password',
          }}
        />

        <Turnstile
          onSuccess={(token) => {
            setTurnstileToken(token);
            setApiError('');
          }}
          onError={(errorCode) => {
            setTurnstileToken(null);
            setApiError('Verification failed. Please try again.');
          }}
          onExpired={() => {
            setTurnstileToken(null);
            setApiError('Verification expired. Please complete it again.');
          }}
          theme="auto"
          size="normal"
        />

        <Button
          type="submit"
          disabled={status === 'loading' || !turnstileToken}
          kind="filled"
          variant="primary"
          py={3}
          mt={2}
        >
          <StatusText fontSize={2} message="Log in" status={status} />
        </Button>
        <GridBox gridGap={3} alignItems="center" fontSize={2} mt={3}>
          {AllowSignup && (
            <Text>
              Don't have an account? <Link to="/sign-up">Sign up here</Link>
            </Text>
          )}
          <Text color={isTcDeluxe ? '#fff' : 'auto'}>
            Forgot your password?{' '}
            <Link to={`/reset-password${window.location.search}`}>
              <Text color={isTcDeluxe ? '#eac554' : 'auto'}>Reset it here</Text>
            </Link>
          </Text>
        </GridBox>
      </Styles.Form>
    </Styles.Wrapper>
  );
};

export default LoginForm;
